import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GamificationProvider } from './context/GamificationContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { Sidebar } from './components/Sidebar';
import { ImproveButton } from './components/ImproveButton';
import { OnboardingGuide } from './components/OnboardingGuide';
import { RegulatoryBanner } from './components/RegulatoryComponents';
import { LegalFooter } from './components/LegalFooter';
import { CookieBanner } from './components/CookieBanner';
import { LoginPage } from './pages/LoginPage';
import { AuthCallback } from './pages/AuthCallback';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { DiagnosisPage } from './pages/DiagnosisPage';
import { MedicationsPage } from './pages/MedicationsPage';
import { MedicalSearchPage } from './pages/MedicalSearchPage';
import { DermatoPage } from './pages/DermatoPage';
import { CotationPage } from './pages/CotationPage';
import { ScoresPage } from './pages/ScoresPage';
import { HistoryPage } from './pages/HistoryPage';
import { BiblioPage } from './pages/BiblioPage';
import { AdminPage } from './pages/AdminPage';
import { LibraryPage } from './pages/LibraryPage';
import { ProgressPage } from './pages/ProgressPage';
// import { PrescriptionPage } from './pages/PrescriptionPage'; // Désactivé temporairement
import { LegalPage } from './pages/LegalPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { BlogIndexPage } from './pages/BlogIndexPage';
import { BlogArticlePage } from './pages/BlogArticlePage';
import { SeoManager } from './components/SeoManager';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { c } = useTheme();

  if (loading) {
    return (
      <div className={`min-h-screen ${c('bgPrimary')} flex items-center justify-center`}>
        <div className={`animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 ${c('textAccent')} border-t-transparent`}></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, c } = useTheme();

  return (
    <div className={`min-h-screen ${c('bgPrimary')} flex flex-col transition-colors duration-300 pb-[var(--regulatory-h)]`}>
      {/* Bandeau réglementaire permanent - non masquable (fixé en bas) */}
      <RegulatoryBanner />
      
      {/* Onboarding Guide for new users */}
      <OnboardingGuide />
      
      <div className="flex flex-1">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        {/* Main content area */}
        <main 
          className={`
            flex-1 min-w-0 flex flex-col transition-all duration-300 min-h-screen
            pt-16 lg:pt-6
            lg:ml-64
            ${sidebarOpen ? 'ml-0' : 'ml-0'}
          `}
        >
          <div className="flex-1 px-4 lg:px-8 pb-6">
            <Routes>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/diagnosis" element={<DiagnosisPage />} />
              <Route path="/medications" element={<MedicationsPage />} />
              <Route path="/medical-search" element={<MedicalSearchPage />} />
              <Route path="/dermato" element={<DermatoPage />} />
              <Route path="/cotation" element={<CotationPage />} />
              <Route path="/scores" element={<ScoresPage />} />
              <Route path="/biblio" element={<BiblioPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/progress" element={<ProgressPage />} />
              {/* Désactivé temporairement : Vérificateur d'Ordonnances */}
              <Route path="/prescription" element={<Navigate to="/dashboard" />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </div>
          {/* Footer légal */}
          <LegalFooter />
        </main>
      </div>

      {/* Bouton flottant d'amélioration (Assistant Progrès) */}
      <ImproveButton />

      {/* Cookie Banner RGPD */}
      <CookieBanner />
    </div>
  );
};

function RoutedApp() {
  const location = useLocation();
  // Détection synchrone du retour OAuth (fragment #session_id=...) AVANT les routes protégées.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/mentions-legales" element={<LegalPage />} />
      <Route path="/confidentialite" element={<PrivacyPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/categorie/:cat" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <AppLayout />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FavoritesProvider>
          <GamificationProvider>
            <BrowserRouter>
              <SeoManager />
              <RoutedApp />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </GamificationProvider>
        </FavoritesProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
